import type {IconProps} from '@primer/octicons-react'
import {ActionList, Button, Truncate} from '@primer/react'
import {Box} from '@primer/styled-react'
import React, {type FunctionComponent, type PropsWithChildren, type ReactNode} from 'react'

import styles from './SharedPicker.module.css'

export type SharedPickerProps = {
  //Props used in the case when there is no selection yet
  leadingIcon?: FunctionComponent<PropsWithChildren<IconProps>>
  hotKey?: string
  //Props used in the case when there is selection
  leadingIconElement?: ReactNode
  sharedPickerMainValue?: ReactNode
  size?: 'small' | 'large'
  // Props used in both cases
  anchorText: string
  anchorProps?: React.HTMLAttributes<HTMLElement>
  ariaLabelledBy?: string
  ariaLabel?: string
  readonly?: boolean
  /**
   * Whether to render the picker as a nested menu item (true) versus a standalone button (false; default).
   */
  nested?: boolean
  ref?: React.Ref<HTMLButtonElement>
  compressedTitle?: string
  className?: string
}
export const SharedPicker = ({
  ref,
  leadingIconElement,
  anchorText,
  anchorProps,
  sharedPickerMainValue,
  ariaLabel,
  readonly,
  size,
  nested = false,
  leadingIcon: LeadingIcon,
  hotKey,
  ariaLabelledBy,
  compressedTitle,
  className,
}: SharedPickerProps) => {
  // Create accessible description for screen readers
  const selectionDescription = React.useMemo(() => {
    if (!sharedPickerMainValue) return undefined

    // Some selection is passed as a string (types, assignees, milestones)
    // and labels as a React element
    // Capping is already implemented when constructing the value, e.g. 5 assignees are passed as `monalisa, test-user, 4+`
    let displayText
    if (typeof sharedPickerMainValue === 'string') {
      displayText = sharedPickerMainValue
    } else {
      displayText = compressedTitle || ''
    }

    return `Currently selected: ${displayText}`
  }, [sharedPickerMainValue, compressedTitle])

  const sharedProps = {
    'aria-labelledby': ariaLabelledBy,
    'aria-label': !ariaLabelledBy ? ariaLabel : undefined,
    'aria-description': selectionDescription,
    // When rendering the `ActionList.Item` instead of a button, we can skip aria-disabled because it is redundant
    // with the `disabled` attribute
    'aria-disabled': nested ? readonly : undefined,
    disabled: readonly,
  }
  if (nested) {
    const leadingIcon = sharedPickerMainValue ? leadingIconElement : LeadingIcon && <LeadingIcon />
    return (
      <ActionList.Item {...anchorProps} {...sharedProps} role="menuitem">
        {leadingIcon ? <ActionList.LeadingVisual>{leadingIcon}</ActionList.LeadingVisual> : null}
        {anchorText}
      </ActionList.Item>
    )
  }
  if (sharedPickerMainValue) {
    return (
      <Button ref={ref} size={size ? size : 'small'} {...anchorProps} {...sharedProps} className={className}>
        <div className={styles.pickerContentContainer}>
          <div className={styles.pickerLabelText}>{anchorText}</div>
          <Box
            sx={{
              display: leadingIconElement ? 'flex' : 'none',
              '> *:not(:last-child)': {},
            }}
            className={styles.leadingIconContainer}
          >
            {leadingIconElement}
          </Box>
          <div className={styles['title']}>
            <Truncate title={compressedTitle ?? sharedPickerMainValue.toString() ?? ''}>
              {sharedPickerMainValue}
            </Truncate>
          </div>
        </div>
      </Button>
    )
    //  This used to be SharedEmptyPicker
  } else {
    return (
      <Button
        ref={ref}
        size={'small'}
        leadingVisual={LeadingIcon}
        trailingVisual={hotKey ? () => <div className={styles.hotkeyBadge}>{hotKey}</div> : undefined}
        className={className ? `${styles.dashedPickerButton} ${className}` : styles.dashedPickerButton}
        {...sharedProps}
        {...anchorProps}
      >
        {anchorText}
      </Button>
    )
  }
}
SharedPicker.displayName = 'SharedPicker'
